import React from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import styles from './styles.less';
import classNames from 'classnames';
import { Modal, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
// resolves to prebuilt dist (no JSX); deep src/ imports would re-add per-build JSX compilation
import { ErrorAlert } from '@graphistry/share-component';

const ColumnPickerTooltip = <Tooltip id="ColumnPickerTooltip">Configure Inspector</Tooltip>;
const DownloadTooltip = <Tooltip id="DownloadTooltip">Download</Tooltip>;

function sortOptions(templates) {
    const sortedTemplates = (templates || []).slice(0);
    sortedTemplates.sort((a, b) => {
        if (!a.identifier || !b.identifier) {
            if (!a.identifier && !b.identifier) {
                return 0;
            }
            if (!a.identifier) {
                return -1;
            }
            return 1;
        }
        const aLower = a.identifier.toLowerCase();
        const bLower = b.identifier.toLowerCase();
        return aLower === bLower ? 0 : aLower < bLower ? -1 : 1;
    });
    return sortedTemplates;
}

const propTypes = {
    id: PropTypes.string,
    name: PropTypes.string,
    options: PropTypes.array,
    value: PropTypes.array,
    allowCreate: PropTypes.bool,
    onChange: PropTypes.func, 
    openTab: PropTypes.string
};

const MultiValueLabel = ({ children, innerProps, data, ...props }) => {
    return (
        <span {...innerProps}>
            <span>{`${data.componentType}:`}</span>
            <span style={{ fontWeight: 'bold' }}>{data.name}</span>
            <span style={{ fontStyle: 'italic', marginLeft: '5px' }}>{data.dataType}</span>
        </span>
    );
};

export class ColumnPicker extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            showConfigModal: false,
            showDownloadModal: false,
            downloadStatus: null,
            errorMessage: null,
            showErrorDetails: false,
            accessDownload: false,
        };
        this.closeConfigModal = this.closeConfigModal.bind(this);
        this.openConfigModal = this.openConfigModal.bind(this);
        this.closeDownloadModal = this.closeDownloadModal.bind(this);
        this.openDownloadModal = this.openDownloadModal.bind(this);
        this.handleDownload = this.handleDownload.bind(this);
        this.toggleErrorDetails = this.toggleErrorDetails.bind(this);
    }

    closeConfigModal() {
        this.setState({ showConfigModal: false });
    }

    openConfigModal() {
        if (!this.props.loading) {
            this.setState({ showConfigModal: true });
        }
    }

    closeDownloadModal() {
        this.setState({ 
            showDownloadModal: false, 
            downloadStatus: null, 
            errorMessage: null,
            showErrorDetails: false 
        });
    }

    async openDownloadModal() {
        const datasetId = (this.props.dataset || {}).dataset;
        if (!datasetId) {
            console.warn('Dataset not yet loaded, cannot open download modal. datasetId:', datasetId);
            return;
        }
        const url = `/api/v2/dataset/access/download/?dataset=${datasetId}`;

        let accessDownload = false;
        
        if (!(this.props.user||{}).isAuthenticated) {
            try {
                const response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                });
        
                if (response.ok) {
                    const result = await response.json();
                    const allowed = result?.data?.allowed;
                    accessDownload = !!allowed;
                }
            } catch (error) {
                console.error("Error checking download access:", error);
            }
        }
    
        this.setState({
            accessDownload,
            showDownloadModal: true
        });
    }

    toggleErrorDetails() {
        this.setState(prevState => ({ showErrorDetails: !prevState.showErrorDetails }));
    }

    async handleDownload(format) {
        const datasetId = (this.props.dataset || {}).dataset;
        if (!datasetId) {
            console.warn('Dataset not yet loaded, cannot download. datasetId:', datasetId);
            this.setState({
                downloadStatus: 'error',
                errorMessage: 'Dataset not yet loaded. Please wait for the graph to finish loading.'
            });
            return;
        }
        const outputType = this.props.openTab === 'edge' ? 'edges' : 'nodes';

        const url = `/api/v2/etl/download/datasets/${datasetId}/gfql/${outputType}`;
        
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    format: format, 
                    gfql_operations: []
                })
            });
        
            if (!response.ok) {
                const responseBody = await response.text();
                this.setState({ 
                    downloadStatus: 'error', 
                    errorMessage: responseBody 
                });
                return;
            }
        
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = `${outputType}.${format}`;
            document.body.appendChild(a);
            a.click();
            a.remove();

            this.setState({ 
                downloadStatus: 'success' 
            });
        } catch (error) {
            this.setState({ 
                downloadStatus: 'error', 
                errorMessage: error.message 
            });
        }
    }
    
    render() {
        const { loading = false } = this.props;
        const options = sortOptions(this.props.options).map(({ identifier, dataType, ...rest }, idx) => ({
            identifier,
            dataType,
            ...rest,
            value: '' + idx,
            label: `${identifier} (${dataType})`
        }));

        const values = this.props.value
            .map(({ identifier }) => options.findIndex(o => o.identifier === identifier))
            .map(idx => options[idx]);

        const isDownloadFeatureEnabled = (this.props.flags || {}).flag_gfql_download;
        const datasetId = (this.props.dataset || {}).dataset;
        const isUserAuthenticated = !!(this.props.user||{}).isAuthenticated;
        const canAccessDownload = isUserAuthenticated || this.state.accessDownload

        return (
            <div className={styles['download-container']}>
                <OverlayTrigger placement="top" delayShow={350} overlay={ColumnPickerTooltip}>
                    <Button onClick={this.openConfigModal} style={this.props.style} variant={'light'}>
                        <i
                            className={classNames(
                                'fa',
                                'fa-fw',
                                loading ? 'fa-spinner' : 'fa-cogs',
                                { 'fa-spin': loading }
                            )}
                        />
                    </Button>
                </OverlayTrigger>
            {isDownloadFeatureEnabled && (
                    <React.Fragment>
                    <OverlayTrigger placement="top" delayShow={350} overlay={DownloadTooltip}>
                        <Button onClick={this.openDownloadModal} style={this.props.style} variant={'light'}>
                            <i className={classNames('fa', 'fa-fw', 'fa-download')} />
                        </Button>
                    </OverlayTrigger>
                    <Modal
                        show={this.state.showConfigModal}
                        onHide={this.closeConfigModal}
                        dialogClassName={styles['column-picker-modal']}>
                        <Modal.Header closeButton>
                            <Modal.Title>Configure Inspector</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Select
                                isMulti
                                isDisabled={this.state.disabled}
                                value={values}
                                placeholder="Pick columns to show"
                                options={options}
                                id={`${this.props.id}_select`}
                                name={`${this.props.name || this.props.id}_select`}
                                components={{ MultiValueLabel }}
                                onChange={values => {
                                    const normalized = (!values ? []
                                        : values instanceof Array ? values
                                            : [values]
                                    ).map(({ value }) => value)
                                        .map(idxStr => options[idxStr]);
                                    return this.props.onChange(normalized);
                                }}
                            />
                        </Modal.Body>
                        <Modal.Footer>
                            <Button onClick={this.closeConfigModal}>Close</Button>
                        </Modal.Footer>
                    </Modal>
                </React.Fragment>
            )}
                <Modal
                    show={this.state.showDownloadModal}
                    onHide={this.closeDownloadModal}
                    dialogClassName={styles['download-modal']}>
                    <Modal.Header closeButton>
                        <Modal.Title>Download Options</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {
                            !canAccessDownload
                            ? <ErrorAlert
                            variant='primary'
                            instructions={<p>
                                To download the dataset data, first
                                <a style={{ margin: '0.5em' }} href={`/accounts/login/?next=/graph/graph.html?dataset=${datasetId}`}><Button>sign in</Button></a>
                                or
                                <a style={{ margin: '0.5em' }} href={`/accounts/signup/?next=/graph/graph.html?dataset=${datasetId}`}><Button>create an account</Button></a>
                            </p>}
                            onDismiss={() => setError(undefined) } />
                            : null
                        }
                        {this.state.downloadStatus === 'success' ? (
                            <div>
                                <i className="fa fa-check-circle" /> Download successful!
                            </div>
                        ) : this.state.downloadStatus === 'error' ? (
                            <div>
                                <i className={`fa fa-exclamation-circle ${styles['download-error-icon']}`}
                                onClick={this.toggleErrorDetails} 
                                /> 
                                There was an error while trying to download.
                                {this.state.showErrorDetails && (
                                    <div className={styles['error-details']}>
                                        {this.state.errorMessage}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className={styles['download-options']}>
                                <Button 
                                    variant={canAccessDownload ? "primary" : "secondary"} 
                                    onClick={() => this.handleDownload('csv')} 
                                    disabled={!canAccessDownload}>
                                    CSV
                                </Button>
                                <Button 
                                    variant={canAccessDownload ? "primary" : "secondary"} 
                                    onClick={() => this.handleDownload('parquet')} 
                                    disabled={!canAccessDownload}>
                                    Parquet
                                </Button>
                                <Button 
                                    variant={canAccessDownload ? "primary" : "secondary"} 
                                    onClick={() => this.handleDownload('json')} 
                                    disabled={!canAccessDownload}>
                                    JSON
                                </Button>
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button onClick={this.closeDownloadModal}>Close</Button>
                    </Modal.Footer>
                </Modal>
            </div>
        );
    }
}

ColumnPicker.propTypes = propTypes;

export default ColumnPicker;
